import React, { useState, useEffect, useRef } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { Toast } from "primereact/toast";
import { Toolbar } from "primereact/toolbar";
import { ProgressSpinner } from "primereact/progressspinner";
import { ColumnGroup } from "primereact/columngroup";
import { Row } from "primereact/row";
import { Tag } from "primereact/tag";
import { getFiscalYears, getTrialBalance } from "../../api/accountingApi";

const TrialBalancePage = () => {
  const toast = useRef(null);
  const dt = useRef(null);
  const [fiscalYears, setFiscalYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [data, setData] = useState([]);
  const [totals, setTotals] = useState({ debit: 0, credit: 0 });
  const [filters, setFilters] = useState({ fiscalYearId: null, fromDate: null, toDate: null });

  useEffect(() => {
    loadFiscalYears();
  }, []);

  const loadFiscalYears = async () => {
    try {
      const fys = await getFiscalYears();
      setFiscalYears(fys);
      const openFy = fys.find((f) => f.status === "open");
      if (openFy) {
        setFilters((prev) => ({ ...prev, fiscalYearId: openFy.id }));
      }
    } catch (err) {
      toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to load fiscal years" });
    } finally {
      setInitialLoading(false);
    }
  };

  const runReport = async () => {
    if (!filters.fiscalYearId) {
      toast.current?.show({ severity: "warn", summary: "Validation", detail: "Select a fiscal year" });
      return;
    }

    try {
      setLoading(true);
      const params = { fiscalYearId: filters.fiscalYearId };
      if (filters.fromDate) params.fromDate = filters.fromDate.toISOString().split("T")[0];
      if (filters.toDate) params.toDate = filters.toDate.toISOString().split("T")[0];

      const result = await getTrialBalance(params);
      setData(result.data || []);
      setTotals(result.totals || { debit: 0, credit: 0 });
    } catch (err) {
      toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to generate trial balance" });
    } finally {
      setLoading(false);
    }
  };

  const currencyTemplate = (value) => {
    if (!value || value === 0) return "-";
    return parseFloat(value).toLocaleString(undefined, { minimumFractionDigits: 2 });
  };

  const typeTemplate = (rowData) => {
    const colors = { Asset: "info", Liability: "warning", Equity: "success", Revenue: "success", Expense: "danger" };
    return <Tag value={rowData.type} severity={colors[rowData.type]} />;
  };

  const isBalanced = Math.abs(totals.debit - totals.credit) < 0.01;

  const footerGroup = data.length > 0 ? (
    <ColumnGroup>
      <Row>
        <Column footer="Totals" colSpan={3} footerStyle={{ textAlign: "right", fontWeight: "bold" }} />
        <Column footer={currencyTemplate(totals.debit)} footerStyle={{ textAlign: "right", fontWeight: "bold" }} />
        <Column footer={currencyTemplate(totals.credit)} footerStyle={{ textAlign: "right", fontWeight: "bold" }} />
      </Row>
      {!isBalanced && (
        <Row>
          <Column
            footer="WARNING: Trial balance is NOT balanced! This indicates a data integrity issue."
            colSpan={5}
            footerStyle={{ textAlign: "center", fontWeight: "bold", color: "var(--red-600)", backgroundColor: "var(--red-50)" }}
          />
        </Row>
      )}
    </ColumnGroup>
  ) : null;

  const leftToolbar = () => (
    <div className="flex flex-wrap gap-2 align-items-center">
      <Dropdown
        value={filters.fiscalYearId}
        options={fiscalYears.map((fy) => ({ label: fy.name, value: fy.id }))}
        onChange={(e) => setFilters({ ...filters, fiscalYearId: e.value })}
        placeholder="Fiscal Year *"
        className="w-12rem"
      />
      <Calendar
        value={filters.fromDate}
        onChange={(e) => setFilters({ ...filters, fromDate: e.value })}
        placeholder="From Date"
        dateFormat="yy-mm-dd"
        showIcon
        className="w-10rem"
      />
      <Calendar
        value={filters.toDate}
        onChange={(e) => setFilters({ ...filters, toDate: e.value })}
        placeholder="To Date"
        dateFormat="yy-mm-dd"
        showIcon
        className="w-10rem"
      />
      <Button label="Run Report" icon="pi pi-play" className="p-button-sm" onClick={runReport} loading={loading} />
    </div>
  );

  const rightToolbar = () => (
    <Button
      label="Export CSV"
      icon="pi pi-download"
      className="p-button-sm p-button-outlined"
      onClick={() => dt.current?.exportCSV()}
      disabled={data.length === 0}
    />
  );

  if (initialLoading) {
    return (
      <div className="flex justify-content-center align-items-center" style={{ height: "60vh" }}>
        <ProgressSpinner />
      </div>
    );
  }

  return (
    <div className="p-3">
      <div className="mb-3">
        <h2 className="m-0">Trial Balance</h2>
        <p className="text-500 mt-1 mb-0">Summary of all account balances for a fiscal period</p>
      </div>

      <Toolbar className="mb-3" left={leftToolbar} right={rightToolbar} />

      <div className="card">
        <DataTable
          ref={dt}
          value={data}
          loading={loading}
          emptyMessage="Run the report to see trial balance data."
          className="p-datatable-sm"
          scrollable
          scrollHeight="calc(100vh - 350px)"
          footerColumnGroup={footerGroup}
        >
          <Column field="code" header="Code" sortable style={{ minWidth: "6rem" }} />
          <Column field="name" header="Account Name" sortable style={{ minWidth: "14rem" }} />
          <Column header="Type" body={typeTemplate} style={{ minWidth: "7rem" }} />
          <Column
            field="debit_balance"
            header="Debit"
            body={(row) => currencyTemplate(row.debit_balance)}
            style={{ minWidth: "8rem", textAlign: "right" }}
            sortable
          />
          <Column
            field="credit_balance"
            header="Credit"
            body={(row) => currencyTemplate(row.credit_balance)}
            style={{ minWidth: "8rem", textAlign: "right" }}
            sortable
          />
        </DataTable>
      </div>

      {data.length > 0 && isBalanced && (
        <div className="mt-2 p-2 bg-green-50 border-round text-green-700 text-center font-bold">
          <i className="pi pi-check-circle mr-2" />Trial Balance is balanced
        </div>
      )}

      <Toast ref={toast} />
    </div>
  );
};

export default TrialBalancePage;
